import { useCallback, useEffect, useState } from 'react';
import { adminRequest, AdminApiError } from './admin-api';
import { resources } from '../../app/src/i18n/resources';
type Photo = {
  id: string;
  username: string | null;
  displayName: string | null;
  avatarUrl: string;
  version: string | null;
};
export function ProfilePhotoModeration({
  getAccessToken,
  onAccessDenied,
}: {
  getAccessToken(): Promise<string>;
  onAccessDenied(): void;
}) {
  const [items, setItems] = useState<Photo[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const failed = useCallback(
    (cause: unknown) => {
      if (cause instanceof AdminApiError && (cause.status === 401 || cause.status === 403)) {
        onAccessDenied();
        return;
      }
      setError('Could not update profile photos. Please refresh and try again.');
    },
    [onAccessDenied],
  );
  const load = useCallback(
    async (after?: string) => {
      setBusy(true);
      setError(null);
      try {
        const data = (await adminRequest(
          getAccessToken,
          `/admin/profile-photos${after ? `?after=${encodeURIComponent(after)}` : ''}`,
        )) as { items: Photo[]; nextCursor: string | null };
        setItems((old) => (after ? [...old, ...data.items] : data.items));
        setCursor(data.nextCursor);
      } catch (cause) {
        failed(cause);
      } finally {
        setBusy(false);
      }
    },
    [getAccessToken, failed],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const hide = async (photo: Photo) => {
    setBusy(true);
    setError(null);
    try {
      await adminRequest(getAccessToken, `/admin/profile-photos/${photo.id}/hide`, {
        method: 'POST',
        body: JSON.stringify({ version: photo.version }),
      });
      setItems((old) => old.filter((item) => item.id !== photo.id));
    } catch (cause) {
      failed(cause);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="min-w-0 space-y-6" aria-busy={busy}>
      <header className="admin-page-header flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-2xl">
          <h1>Profile photos</h1>
          <p className="mt-3 text-sm leading-6 text-ink-soft">
            Hide inappropriate photos from all reader-facing surfaces. The reader can upload a
            replacement.
          </p>
        </div>
        <button
          disabled={busy}
          onClick={() => void load()}
          className="admin-button shrink-0"
          type="button"
        >
          Refresh
        </button>
      </header>
      {error ? (
        <p
          className="rounded-xl border border-danger/25 bg-danger-fill/40 p-4 text-sm leading-6 text-danger"
          role="alert"
        >
          {error}
        </p>
      ) : null}
      {busy && !items.length ? (
        <p className="admin-empty" role="status">
          {resources.en.translation.boardAdmin.loading}
        </p>
      ) : null}
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((photo) => (
          <article key={photo.id} className="admin-panel overflow-hidden">
            <div className="grid min-h-48 place-items-center bg-canvas/55 p-7">
              <img
                src={photo.avatarUrl}
                alt=""
                referrerPolicy="no-referrer"
                className="size-32 rounded-full border-4 border-panel-raised object-cover shadow-sm"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 p-5">
              <p className="min-w-0 break-words text-sm font-semibold">
                {photo.username ? `@${photo.username}` : (photo.displayName ?? 'Reader')}
              </p>
              <button
                disabled={busy}
                onClick={() => void hide(photo)}
                className="admin-button-danger"
                type="button"
              >
                Hide photo
              </button>
            </div>
          </article>
        ))}
      </div>
      {!busy && !error && !items.length ? (
        <p className="admin-empty">No profile photos to review.</p>
      ) : null}
      {cursor ? (
        <button
          className="admin-button"
          disabled={busy}
          onClick={() => void load(cursor)}
          type="button"
        >
          Load more
        </button>
      ) : null}
    </section>
  );
}
